import {Layers, RockBottomLayers, SimpleStageLayer} from "./RockBottomLayers";
import {RockBottom} from "../RockBottom";
import {PolaroidSymbol} from "./PolaroidSymbol";
import {RockBottomGameConfig} from "../RockBottomGameConfig";
import {RockBottomParsedGameData} from "../server/data/RockBottomParsedGameData";
import {RockBottomParsedInitData} from "../server/data/RockBottomParsedInitData";
import {SpinePlayer} from "../../core/utils/SpinePlayer";
import {gsap} from "gsap";
import {RockBottomGameAssets} from "../RockBottomGameAssets";
import {RockBottomSound} from "../sound/RockBottomSound";
import {MathHelper} from "../../core/utils/MathHelper";
import {RockBottomGameMode} from "../server/RockBottomGameMode";

export class RockBottomFreeSpinView extends PIXI.Container {
    private _layer: SimpleStageLayer | undefined;
    private _panelLeft: PIXI.Container;
    private _panelRight: PIXI.Container;
    private _polaroids: PolaroidSymbol[] = [];
    private _wineBottle: SpinePlayer;
    private _normalBottle: PIXI.Sprite;
    private _crackedBottle: PIXI.Sprite;
    private _currWineLevel: number = 0;
    private _maxWineLevel: number = 20;
    private _spinsCounter: PIXI.BitmapText;
    private _wineColor: number[] = [0x905872, 0x589076, 0x706088, 0x9A794C, 0x8E4648];

    constructor() {
        super();
        this.init();
    }

    public static freespinSounds(level: number): void {
        const data: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData();
        if (data.mode === RockBottomGameMode.FREESPIN || data.nextMode === RockBottomGameMode.FREESPIN) {
            RockBottomSound.switchMusic({
                name: RockBottomSound.FREESPIN_MUSIC_LEVEL[level],
                repeat: Number.MAX_VALUE
            });
        } else if (data.mode === RockBottomGameMode.SUPER_FREESPIN || data.nextMode === RockBottomGameMode.SUPER_FREESPIN) {
            RockBottomSound.switchMusic({
                name: RockBottomSound.SUPER_FREESPIN_MUSIC_LEVEL[level],
                repeat: Number.MAX_VALUE
            });
        }
    }

    public showView(): void {
        this.visible = true;
        this._wineBottle.visible = true;
        this._normalBottle.visible = true;
        this._spinsCounter.visible = false;
        this.setActivePolaroid();
        this.updateBottleLevel();
        const parsedData: RockBottomParsedInitData | RockBottomParsedGameData = RockBottom.getParsedData();
        this.updateSpinsCounter(parsedData.freespinsLeft);
    }

    public hideView(): void {
        this.visible = false;
        this._wineBottle.visible = false;
        this._normalBottle.visible = false;
        this._crackedBottle.visible = false;
        this._spinsCounter.visible = false;
        RockBottom.midSymExpand.removeExpandAnims();
    }

    public onReelStopped(reelId: number): gsap.core.Timeline {
        const parsedData: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData(),
            midSymArray: string[] = (parsedData.reels[reelId].slice(1, -1)).filter(element => !["X", "S"].includes(element)),
            tl: gsap.core.Timeline = gsap.timeline();

        for (let i: number = 0; i < midSymArray.length; i++) {
            const index: number = RockBottomGameConfig.MID_SYMS.indexOf(midSymArray[i]),
                polaroidSym: PolaroidSymbol = this._polaroids[index],
                currMultValue: number = polaroidSym.getCurrMultValue;

            if (currMultValue === 1 && index !== 4 && index !== Math.abs(parsedData.fsLevel - 4)) {
                RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.MULTIPLIER_ACTIVATE});
            }

            tl.add(RockBottom.midSymExpand.playExpandAnim(reelId, index));
            polaroidSym.updateMultiplier(currMultValue + 1);
        }

        return tl;
    }

    public resetUI(): void {
        for (let i: number = 0; i < this._polaroids.length; i++) {
            this._polaroids[i].resetMultiplier();
        }
    }

    public onScatterLanding(scatterCount: number): void {
        this.updateBottleLevel(scatterCount);
        this.updateSpinsCounter();
    }

    public setActivePolaroid(isUpdate: boolean = false): void {
        const parsedData: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData();
        if (this.visible) {
            const indexToSelect: number = (RockBottomGameConfig.MID_SYMS.length - 1) - (isUpdate ? parsedData.fsLevelNextSpin : parsedData.fsLevel);

            for (let i: number = 0; i < this._polaroids.length; i++) {
                if (i === indexToSelect) {
                    this._polaroids[i].setAsActive(true);
                    RockBottom.midSymExpand.setActiveSymbol(i);
                } else {
                    if (i > indexToSelect) {
                        this._polaroids[i].setWornOut();
                    } else {
                        this._polaroids[i].setAsActive(false);
                    }
                }
                this._polaroids[i].updateMultiplier();
            }
        }
    }

    public updateSpinsCounter(val: number = 0, isNewSpin: boolean = false, isForMainGame: boolean = false): void {
        this._spinsCounter.visible = isForMainGame;
        if (val) {
            this._spinsCounter.text = val.toString();
        } else {
            const prevVal: string = this._spinsCounter.text;
            if (isNewSpin) {
                this._spinsCounter.text = (parseInt(prevVal) - 1).toString();
            } else {
                this._spinsCounter.text = (parseInt(prevVal) + 1).toString();
            }
        }

        this._spinsCounter.pivot.set(this._spinsCounter.width / 2, this._spinsCounter.height / 2);

        if (RockBottom.inBonusGame) {
            RockBottom.keypadPlugin.setZeroBetSpinCounter(parseInt(this._spinsCounter.text));
        }
    }

    public playEmptyBottleAnim(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(this._wineBottle.play({name: "level06"}));

        return tl;
    }

    public updateBottleLevel(currScatterCount?: number, isAfterDrinkAnim: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            parsedData: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData(),
            totalScatterCollectedSoFar: number = parsedData.collectedScatters - (parsedData.scatterPositions.filter(x => x > -1).length),
            collectedScatters = totalScatterCollectedSoFar > this._maxWineLevel ? 0 : parsedData.collectedScatters,
            modulusValue: number = collectedScatters % 5;
        let randomNo: number = MathHelper.uniqueNumberInRange(0, 1);

        if (collectedScatters) {
            if (currScatterCount) {
                this._currWineLevel++;
                RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.BOTTLE_FILL[randomNo]});
            } else {
                if (modulusValue) {
                    this._currWineLevel = modulusValue;
                } else {
                    if (parsedData.isRestoreState) {
                        this._currWineLevel = 0;
                    } else {
                        this._currWineLevel = 0;
                    }
                }
            }
        } else {
            this._currWineLevel = 0;
        }

        if (!(this._crackedBottle.visible) && (isAfterDrinkAnim || parsedData.isRestoreState) && (parsedData.fsLevelNextSpin === 4)) {
            this._crackedBottle.visible = true;
            this._currWineLevel = 0;
        }

        if (this._currWineLevel) {
            if (this._currWineLevel <= 5) {
                tl.add(this._wineBottle.play({
                    name: `level0${this._currWineLevel}`,
                    tintColor: (this._currWineLevel === 5) ? this._wineColor[parsedData.fsLevel] : 0xffffff
                }));
            }
        } else {
            this._wineBottle.reset();
        }

        return tl;
    }

    public tintFilledBottle(): void {
        const parsedData: RockBottomParsedInitData | RockBottomParsedGameData = RockBottom.getParsedData();
        this._wineBottle.applyTint(this._wineColor[parsedData.fsLevel]);
    }

    private init() {
        this._layer = RockBottomLayers.getLayer(Layers.FS_STATIC_VIEW);
        this.createReelPanels();
        this.createPanelSymbols();
        this.createBottle();
        this.createSpinsCounter();
        this._layer?.addChild(this);
        this.hideView();
    }

    private createReelPanels(): void {
        this._panelLeft = this.createReelPanel("left");
        this._panelRight = this.createReelPanel("right");

        this.addChild(this._panelLeft, this._panelRight);
    }

    private createReelPanel(side: string): PIXI.Container {
        const reelPanel: PIXI.Container = new PIXI.Container(),
            reelBg: PIXI.Sprite = new PIXI.Sprite(RockBottom.assetLoader.getTextures("reelPanel")[0]);

        if (side === "left") {
            reelPanel.position.set(72, 360);
        } else {
            reelPanel.position.set(647, 360);
        }
        reelBg.anchor.set(0.5, 0.5);
        reelPanel.addChild(reelBg);

        return reelPanel;
    }

    private createPanelSymbols(): void {
        const midSymArray: string[] = RockBottomGameConfig.MID_SYMS,
            symLength: number = midSymArray.length;

        for (let i: number = 0; i < midSymArray.length; i++) {
            this._polaroids.push(new PolaroidSymbol(midSymArray[i]));
        }

        for (let i: number = 0; i < symLength; i++) {
            const container: PIXI.Container = i > 1 ? this._panelLeft : this._panelRight;
            container.addChild(this._polaroids[i]);
        }
    }

    private createBottle(): void {
        this._wineBottle = new SpinePlayer("Wine_Bottle");
        this._wineBottle.position.set(616, 70);

        this._normalBottle = new PIXI.Sprite(RockBottom.assetLoader.getTextures("normalBottle")[0]);
        this._normalBottle.position.set(616, 70);
        this._normalBottle.anchor.set(0.5);
        this._normalBottle.visible = false;

        this._crackedBottle = new PIXI.Sprite(RockBottom.assetLoader.getTextures("crackedBottle")[0]);
        this._crackedBottle.position.set(616, 70);
        this._crackedBottle.anchor.set(0.5);
        this._crackedBottle.visible = false;

        this.addChild(this._normalBottle, this._wineBottle, this._crackedBottle);
    }

    private createSpinsCounter(): void {
        this._spinsCounter = new PIXI.BitmapText("", {
            fontName: RockBottomGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 30,
            letterSpacing: -30
        });
        this._spinsCounter.position.set(670, 90);
        this._spinsCounter.scale.set(RockBottomGameConfig.SPINS_COUNT_SCALE);
        this._spinsCounter.visible = false;

        this._layer?.addChild(this._spinsCounter);
    }
}